import { Button, LoadIndicator, ScrollView, SelectBox } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import { formatMessage } from 'devextreme/localization';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import DataGridMVP from '../../DataGridMVP';
import styles from './messagesAdmin.module.scss';
import HeaderButtonMVP from '../../HeaderButtonMVP';
import cMetDataGrid from './1cMetDataGrid';
import met1cDataGrid from './met1cDataGrid';
import metSapDataGrid from './metSapDataGrid';
import sapMetDataGrid from './sapMetDataGrid';
import PopupMVP from '../../PopupMVP';

const directions = ['MET_SAP', 'C1_MET', 'MET_1C', 'SAP_MET'];

const setInitialFilterData = (plantId, localTemp) => (
  {
    plantId,
    filter: localTemp ? localTemp.searchText : '',
    dateFrom: null,
    dateTo: null
  });

const MessagesAdmin = ({
  plantId,
  selectedIndex,
  adminMessagesReducer,
  getMessagesFilter,
  getMessages,
  getMessageTypeFilter,
  getMessageXml,
  resetMessages,
  tabsReducer: { tabSelect }
}) => {
  const localTemp = JSON.parse(localStorage.getItem('dataGridAdminMessagesData'));

  const byPanelTab = tabSelect === 13;
  const [selectedDirection, setSelectedDirection] = useState(byPanelTab ? 'C1_MET' : 'MET_SAP');
  const [selectedMessageType, setSelectedMessageType] = useState('N');
  const [filterData, setFilterData] = useState(setInitialFilterData(byPanelTab ? plantId : '', localTemp));

  const [focusedRowData, setFocusedRowData] = useState(null);
  const [headButtVisibol, setHeadButtVisibol] = useState(false);
  const [visiblePopupXml, setVisiblePopupXml] = useState(false);
  const [xmlCopied, setXmlCopied] = useState(false);
  const msgTypeFilter = ['N', ...Object.keys(adminMessagesReducer.messageTypeFilter)];

  useEffect(() => {
    setHeadButtVisibol(Boolean(focusedRowData));
  }, [focusedRowData]);

  useEffect(() => {
    setSelectedDirection(byPanelTab ? 'C1_MET' : 'MET_SAP');
  }, [byPanelTab, plantId]);

  const prevSelectedDirection = useRef(selectedDirection);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const type = prevSelectedDirection.current !== selectedDirection ? 'N' : selectedMessageType;
    prevSelectedDirection.current = selectedDirection;

    if (isFirstRender.current) {
      getMessages({ filter: { ...filterData, plantId }, direction: byPanelTab ? 'C1_MET' : 'MET_SAP', type: 'N' });
      getMessageTypeFilter(byPanelTab ? 'C1_MET' : 'MET_SAP');
      isFirstRender.current = false;
    } else if (byPanelTab && selectedIndex !== 3) {
      getMessages({ filter: { ...filterData, plantId }, direction: selectedDirection, type });
      getMessageTypeFilter(selectedDirection);
    } else if (!byPanelTab && selectedIndex === 3) {
      getMessages({ filter: filterData, direction: selectedDirection, type });
      getMessageTypeFilter(selectedDirection);
    }
  }, [
    getMessages,
    selectedIndex,
    getMessageTypeFilter,
    byPanelTab,
    selectedDirection,
    filterData,
    selectedMessageType,
    plantId
  ]);

  const onSelectOptionChanged = useCallback(({ value }) => {
    setSelectedDirection(value);
    setSelectedMessageType('N');
  }, []);

  const onSelectedMessageTypeChanged = useCallback(({ value }) => {
    setSelectedMessageType(value);
  }, []);

  const onFocusedRowChanged = useCallback(({ row }) => {
    if (row) {
      setFocusedRowData(row.data);
    }
  }, []);

  const onClickResetMessages = useCallback(async () => {
    const { msgCounter } = focusedRowData;
    await resetMessages({ msgCounter, type: selectedDirection });
    getMessages({
      filter: filterData,
      direction: selectedDirection,
      type: selectedMessageType
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedRowData, resetMessages, selectedDirection]);

  const onClickCheckXml = useCallback(() => {
    setVisiblePopupXml(true);
    const { msgCounter } = focusedRowData;
    getMessageXml({ msgCounter, type: selectedDirection });
  }, [
    focusedRowData,
    getMessageXml,
    selectedDirection
  ]);

  const onClickXmlCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(adminMessagesReducer.messageXmlData);
      setXmlCopied(true);
    } catch (err) {
      console.error('Ошибка копирования', err);
      setXmlCopied(false);
    }
  }, [adminMessagesReducer.messageXmlData]);

  const onXmlPopupHiding = useCallback(() => {
    setVisiblePopupXml(false);
    setXmlCopied(false);
  }, []);

  const dataGridOnOptionChanged = useCallback(({ value, name }) => {
    if (name === 'searchPanel') {
      setFilterData((prev) => ({ ...prev, filter: value }));
    }
  }, []);

  const onSetFilterDataGrid = useCallback(
    (value) => {
      setFilterData((prev) => ({ ...prev, ...value }));
    },
    []
  );

  const onClickUpdate = useCallback(() => {
    if (!adminMessagesReducer.messagesListGet.loading) {
      getMessages({
        filter: filterData,
        direction: selectedDirection,
        type: selectedMessageType
      });
    }
  },
  [
    filterData,
    getMessages,
    selectedDirection,
    selectedMessageType,
    adminMessagesReducer.messagesListGet.loading
  ]);

  const onCellClick = useCallback((e) => {
    if (e.column && e.column.dataField === 'msgRemark') {
      const range = document.createRange();
      range.selectNodeContents(e.cellElement);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  const render1cMetDataGrid = useMemo(
    () =>
      cMetDataGrid.map((item) => (
        <Column
          dataField={item.dataField}
          caption={formatMessage('textTableAdminMessages')[item.dataField]}
          visible={!item.notVisible}
          allowSearch={false}
          allowFiltering
          cellRender={item.cellRender}
          key={item.dataField}
          dataType={item.dataType}
          cssClass={item.cssClass}
          width={item.dataField === 'msgRemark' ? 200 : 100}
        />
      )),
    []
  );

  const renderMet1cDataGrid = useMemo(
    () =>
      met1cDataGrid.map((item) => (
        <Column
          dataField={item.dataField}
          caption={formatMessage('textTableAdminMessages')[item.dataField]}
          visible={!item.notVisible}
          allowSearch={false}
          allowFiltering
          cellRender={item.cellRender}
          key={item.dataField}
          dataType={item.dataType}
          cssClass={item.cssClass}
          width={item.dataField === 'msgRemark' ? 200 : 100}
        />
      )),
    []
  );

  const renderMetSapDataGrid = useMemo(
    () =>
      metSapDataGrid.map((item) => (
        <Column
          dataField={item.dataField}
          caption={formatMessage('textTableAdminMessages')[item.dataField]}
          visible={!item.notVisible}
          allowSearch={false}
          allowFiltering
          cellRender={item.cellRender}
          key={item.dataField}
          dataType={item.dataType}
          cssClass={item.cssClass}
          width={item.dataField === 'msgRemark' ? 200 : 'auto'}
        />
      )),
    []
  );

  const renderSapMetDataGrid = useMemo(
    () =>
      sapMetDataGrid.map((item) => (
        <Column
          dataField={item.dataField}
          caption={formatMessage('textTableAdminMessages')[item.dataField]}
          visible={!item.notVisible}
          allowSearch={false}
          allowFiltering
          cellRender={item.cellRender}
          key={item.dataField}
          dataType={item.dataType}
          cssClass={item.cssClass}
          width={item.dataField === 'msgRemark' ? 200 : 'auto'}
        />
      )),
    []
  );

  return (
    <>
      <div className={`${styles.dataGridWrapper} adminMessages`}>
        <HeaderButtonMVP headButtVisibol={{ display: 'flex', top: '14px' }} classname={styles.headerWrapper}>
          <SelectBox
            dataSource={byPanelTab ? ['C1_MET', 'MET_1C'] : directions}
            displayExpr={(value) => formatMessage('directions')[value]}
            type="normal"
            className={styles.directionSelect}
            stylingMode="outlined"
            value={selectedDirection}
            onValueChanged={onSelectOptionChanged}
            deferRendering
            disabled={adminMessagesReducer.messagesListGet.loading}
          />
          <SelectBox
            dataSource={msgTypeFilter}
            displayExpr={(value) => value === 'N'
              ? 'Не выбрано'
              : `${value} - ${adminMessagesReducer.messageTypeFilter[value]}`}
            type="normal"
            className={styles.messageTypeFilterSelect}
            stylingMode="outlined"
            value={selectedMessageType}
            onValueChanged={onSelectedMessageTypeChanged}
            deferRendering
            disabled={adminMessagesReducer.messagesListGet.loading}
          />
          {headButtVisibol
            && (
            <>
              <Button
                type="danger"
                stylingMode="contained"
                onClick={onClickResetMessages}
                text={formatMessage('textButton').reset}
              />

              <Button
              type="default"
              stylingMode="contained"
              onClick={onClickCheckXml}
              text={formatMessage('textButton').checkXml}
              />
            </>
            )}
        </HeaderButtonMVP>
        <DataGridMVP
          dataSource={adminMessagesReducer.messagesList}
          dataGridOnOptionChanged={dataGridOnOptionChanged}
          className={`adminMessages ${styles.dataGrid}`}
          keyExpr="msgCounter"
          setStateStoringName={'dataGridAdminMessagesData'}
          isDataLoading={adminMessagesReducer.messagesListGet.loading}
          tText={formatMessage('textPeriod')}
          selectionMode="none"
          focusedRowEnabled
          visibleButtonUpdate
          onFocusedRowChanged={onFocusedRowChanged}
          visibleInfoIcon={false}
          visibleSummary={false}
          filterDataGrid={filterData}
          setFilterDataGrid={onSetFilterDataGrid}
          onClickUpdate={onClickUpdate}
          onCellClick={onCellClick}
        >
          {selectedDirection === 'MET_SAP' && renderMetSapDataGrid}
          {selectedDirection === 'C1_MET' && render1cMetDataGrid}
          {selectedDirection === 'MET_1C' && renderMet1cDataGrid}
          {selectedDirection === 'SAP_MET' && renderSapMetDataGrid}
        </DataGridMVP>
      </div>
      <PopupMVP
        visible={visiblePopupXml}
        onHiding={onXmlPopupHiding}
        title="XML"
        height={470}
      >
        <div className="formData">
          {adminMessagesReducer.messageXmlGet.loading ? (
            <LoadIndicator />
          ) : (
            <ScrollView height={320} direction="both">
              <pre>
                {adminMessagesReducer.messageXmlData}
              </pre>
            </ScrollView>
          )}
          <div className={`formButton ${styles.xmlModalBtns}`}>
            <Button
              type="default"
              stylingMode={xmlCopied ? 'outlined' : 'contained'}
              text={xmlCopied ? `${formatMessage('textButton').copied}!` : formatMessage('textButton').copyAll}
              width={200}
              onClick={onClickXmlCopy}
            />
            <Button
              type="default"
              stylingMode="contained"
              text={formatMessage('textButton').confirm}
              width={200}
              onClick={onXmlPopupHiding}
            />
          </div>
        </div>
      </PopupMVP>
    </>
  );
};

export default React.memo(MessagesAdmin);
